import publicNotificationsApi from '@/api/publicnotification'
import moment from 'moment'
import { parseToManilaTime, formatWithLocalTimezone } from '@/helpers/date-helpers'
import auth from '@/auth/auth.js'

export class MaintenanceTracker {
    constructor(onStateChangeCallback) {
        this.onStateChange = onStateChangeCallback
        this.activationTimer = null
        this.expirationTimer = null
        this.ACTIVATION_THRESHOLD_MS = 10800000 // 3 hours in milliseconds
    }

    async checkStatus() {
        try {
            let notifications = []
            
            // 1. Always check local storage first for a lightning-fast UI state evaluation
            const cachedData = localStorage.getItem('publicNotifications')
            if (cachedData) {
                notifications = JSON.parse(cachedData)
                this.evaluateMaintenance(notifications)
            }

            // 2. CRITICAL OPTIMIZATION: If the user is logged in, completely stop here.
            // Do not call the API, and do not let other notification categories clear your cache.
            // SignalR handles real-time emergency triggers for logged-in sessions.
            if (auth.loggedIn()) {
                return 
            }

            // 3. Public/Logged-out users: Keep calling the API to ensure they get the latest public banners
            const response = await publicNotificationsApi.getNowShowing()
            if (response && response.status === 200 && response.data.nowShowing) {
                notifications = response.data.nowShowing
                
                localStorage.setItem('publicNotifications', JSON.stringify(notifications))
                this.evaluateMaintenance(notifications)
            }
        } catch (error) {
            console.error('Maintenance Tracker Error:', error)
        }
    }
    
    evaluateMaintenance(notifications) {
        this.clearAllTimers()

        if (!Array.isArray(notifications)) return

        const maintenanceNotice = notifications.find(
            item => item.categoryName === 'System Maintenance'
        )

        if (maintenanceNotice) {
            const now = moment()
            const start = parseToManilaTime(maintenanceNotice.effectiveDate)
            const end = parseToManilaTime(maintenanceNotice.expirationDate)

            // 1. Maintenance is ACTIVE right now
            if (now.isBetween(start, end, null, '[]')) {
                const msRemaining = end.diff(now)
                const formattedEndTime = formatWithLocalTimezone(end)

                this.onStateChange({ 
                    status: 'ACTIVE', 
                    formattedEndTime: formattedEndTime 
                })

                if (msRemaining > 0) {
                    this.expirationTimer = setTimeout(() => {
                        this.handleMaintenanceEnd()
                    }, msRemaining)
                }
                return
            }

            // 2. Maintenance is PENDING (Within 3-hour window)
            if (now.isBefore(start)) {

                const msUntilStart = start.diff(now)

                if (msUntilStart <= this.ACTIVATION_THRESHOLD_MS) {

                    this.onStateChange({ status: 'PENDING' })

                    this.activationTimer = setTimeout(() => {
                        this.checkStatus()
                    }, msUntilStart)

                    return
                }
            }
        }

        // 3. No Maintenance active or pending
        this.onStateChange({ status: 'NONE' })
    }

    handleMaintenanceEnd() {
        localStorage.removeItem('publicNotifications')
        this.onStateChange({ status: 'EXPIRED' })
    }

    clearAllTimers() {
        if (this.activationTimer) clearTimeout(this.activationTimer)
        if (this.expirationTimer) clearTimeout(this.expirationTimer)
        this.activationTimer = null
        this.expirationTimer = null
    }
}
